import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from "react-simple-maps";
import { Info, ArrowUp, ArrowDown, Minus, RefreshCw, Loader2, CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";

export const Route = createFileRoute("/_admin/analytics/dashboard")({
  head: () => ({
    meta: [
      { title: "Website Analytics Dashboard — Three Day Launch Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AnalyticsDashboardPage,
});

type DateRangeKey = "7d" | "28d" | "90d" | "custom";

interface CustomRange {
  start: string;
  end: string;
}

function toIso(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

interface DashboardData {
  range: DateRangeKey;
  fetchedAt: string;
  fromCache: boolean;
  gaError: string | null;
  gscError: string | null;
  summary: {
    users: number;
    sessions: number;
    pageViews: number;
    engagementRate: number;
    prevUsers: number;
    prevSessions: number;
    prevPageViews: number;
    prevEngagementRate: number;
  } | null;
  timeseries: { date: string; sessions: number; views: number }[];
  topPages: { path: string; views: number; users: number; avgEngagementSeconds: number }[];
  sources: { channel: string; sessions: number }[];
  devices: { device: string; sessions: number }[];
  geo: { country: string; sessions: number; views: number }[];
  searchTotals?: { clicks: number; impressions: number; ctr: number; position: number } | null;
  queriesByClicks: { query: string; clicks: number; impressions: number; ctr: number; position: number }[];
  queriesByImpressions: { query: string; clicks: number; impressions: number; ctr: number; position: number }[];
  searchPages: { page: string; clicks: number; impressions: number; ctr: number; position: number }[];
  quickWins: { type: string; title: string; detail: string; metric?: number; change?: number }[];
  formConversions?: {
    users: number;
    prevUsers: number;
    forms: FormConversionRow[];
    total: FormConversionRow;
  } | null;
}

interface FormConversionRow {
  formType: string;
  label: string;
  submissions: number;
  prevSubmissions: number;
  rate: number;
  prevRate: number;
  hasPrevData: boolean;
}

interface Recommendations {
  items: { title: string; detail?: string }[];
  spotlight?: { title: string; detail?: string }[];
  updatedAt: string;
}

type ApiResponse =
  | { status: "ok"; data: DashboardData; refreshesUsedToday: number; limit: number; recommendations?: Recommendations | null }
  | { status: "selection_required"; candidates: string[] }
  | { status: "rate_limited"; refreshesUsedToday: number; limit: number; data: DashboardData | null; recommendations?: Recommendations | null }
  | { error: string };

function normalizeDashboardData(input: DashboardData): DashboardData {
  return {
    ...input,
    timeseries: input.timeseries ?? [],
    topPages: input.topPages ?? [],
    sources: input.sources ?? [],
    devices: input.devices ?? [],
    geo: input.geo ?? [],
    queriesByClicks: input.queriesByClicks ?? [],
    queriesByImpressions: input.queriesByImpressions ?? [],
    searchPages: input.searchPages ?? [],
    quickWins: input.quickWins ?? [],
  };
}

const PIE_COLORS = ["#2563eb", "#16a34a", "#f59e0b", "#dc2626", "#7c3aed", "#0891b2", "#db2777", "#65a30d"];

function pctChange(current: number, previous: number): string | null {
  if (!previous) return null;
  const change = ((current - previous) / previous) * 100;
  const sign = change >= 0 ? "+" : "";
  return `${sign}${change.toFixed(1)}%`;
}

function fmtDateLabel(yyyymmdd: string): string {
  if (yyyymmdd.length === 8) {
    return `${yyyymmdd.slice(4, 6)}/${yyyymmdd.slice(6, 8)}`;
  }
  return yyyymmdd;
}

function fmtDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function shortPath(url: string): string {
  return url.replace(/^https?:\/\/(www\.)?threedaylaunch\.com/, "") || "/";
}

function fmtLocalTimestamp(isoString: string): string {
  const d = new Date(isoString);
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(d);
}

function fmtDateRangeLabel(range: DateRangeKey, custom?: CustomRange | null): string {
  const fmt = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" });
  if (range === "custom" && custom) {
    return `${fmt.format(new Date(`${custom.start}T12:00:00`))} – ${fmt.format(new Date(`${custom.end}T12:00:00`))}`;
  }
  const days = range === "7d" ? 7 : range === "90d" ? 90 : 28;
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));
  return `${fmt.format(start)} – ${fmt.format(end)}`;
}

function SectionNote({ children }: { children: React.ReactNode }) {
  return <p className="mt-1 text-sm text-muted-foreground">{children}</p>;
}

function ConversionCell({ row }: { row: FormConversionRow }) {
  if (!row.hasPrevData) {
    return <span className="text-muted-foreground">No prior data</span>;
  }
  const diff = row.rate - row.prevRate;
  const up = diff >= 0;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        up
          ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
          : "bg-red-500/10 text-red-600 dark:text-red-400"
      }`}
    >
      {up ? "+" : ""}
      {diff.toFixed(2)} pts
    </span>
  );
}

function FormConversionsSection({
  conversions,
}: {
  conversions: NonNullable<DashboardData["formConversions"]>;
}) {
  const rows = [...conversions.forms, conversions.total];
  return (
    <section className="rounded-lg border border-border bg-card p-6">
      <h2 className="text-lg font-semibold text-foreground">Form Opt-In Conversions</h2>
      <SectionNote>
        How many visitors filled out each form on the site. Conversion rate is form submissions
        divided by total users ({conversions.users.toLocaleString()} this period,{" "}
        {conversions.prevUsers.toLocaleString()} in the previous period), compared to the same
        length of time before this range.
      </SectionNote>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4">Form</th>
              <th className="py-2 pr-4">Submissions</th>
              <th className="py-2 pr-4">Previous</th>
              <th className="py-2 pr-4">Conversion Rate</th>
              <th className="py-2">Change</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.formType}
                className={`border-b border-border/50 ${
                  row.formType === "__total" ? "font-semibold" : ""
                }`}
              >
                <td className="py-2 pr-4 whitespace-nowrap">{row.label}</td>
                <td className="py-2 pr-4">{row.submissions.toLocaleString()}</td>
                <td className="py-2 pr-4 text-muted-foreground">
                  {row.hasPrevData ? row.prevSubmissions.toLocaleString() : "—"}
                </td>
                <td className="py-2 pr-4">{row.rate.toFixed(2)}%</td>
                <td className="py-2 whitespace-nowrap">
                  <ConversionCell row={row} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

type SortDir = "asc" | "desc";

interface SortState<K extends string> {
  key: K | null;
  dir: SortDir;
}

function useSort<T extends Record<string, unknown>>(items: T[]) {
  const [sort, setSort] = useState<SortState<Extract<keyof T, string>>>({
    key: null,
    dir: "desc",
  });

  const sorted = useMemo(() => {
    if (!sort.key) return items;
    const key = sort.key;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
      const av = a[key] as number | string;
      const bv = b[key] as number | string;
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [items, sort]);

  const toggle = useCallback(
    (key: Extract<keyof T, string>) => {
      setSort((prev) => ({
        key,
        dir: prev.key === key && prev.dir === "desc" ? "asc" : "desc",
      }));
    },
    [setSort]
  );

  return { sorted, sort, toggle };
}

function SortHeader({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1 whitespace-nowrap py-2 pr-3 text-left font-medium text-muted-foreground hover:text-foreground"
    >
      {label}
      {active && (dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
    </button>
  );
}

function MetricTooltip({ children, text }: { children: React.ReactNode; text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex items-center gap-1">
      {children}
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        aria-label="More info"
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {open && (
        <span className="absolute bottom-full left-1/2 z-10 mb-2 w-48 -translate-x-1/2 rounded-md border border-border bg-popover px-2 py-1.5 text-xs text-popover-foreground shadow-lg">
          {text}
        </span>
      )}
    </span>
  );
}

function AnalyticsDashboardPage() {
  const [range, setRange] = useState<DateRangeKey>("28d");
  const [data, setData] = useState<DashboardData | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendations | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshesUsed, setRefreshesUsed] = useState(0);
  const [refreshLimit, setRefreshLimit] = useState(5);
  const [siteCandidates, setSiteCandidates] = useState<string[] | null>(null);
  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const [custom, setCustom] = useState<CustomRange | null>(null);
  const [pickerRange, setPickerRange] = useState<DateRange | undefined>(undefined);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = useCallback(
    async (
      opts: {
        force?: boolean;
        rangeKey?: DateRangeKey;
        gscSiteUrl?: string;
        customRange?: CustomRange | null;
      } = {}
    ) => {
      const rangeKey = opts.rangeKey ?? range;
      const customRange = opts.customRange !== undefined ? opts.customRange : custom;
      if (opts.force) setRefreshing(true);
      else setLoading(true);
      setError(null);
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;
        if (!token) throw new Error("Not signed in");

        const res = await fetch("/api/admin/analytics", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            range: rangeKey,
            force: opts.force ?? false,
            ...(opts.gscSiteUrl ? { gscSiteUrl: opts.gscSiteUrl } : {}),
            ...(rangeKey === "custom" && customRange
              ? { customStart: customRange.start, customEnd: customRange.end }
              : {}),
          }),
        });
        const json = (await res.json()) as ApiResponse;

        if ("error" in json) throw new Error(json.error);

        if (json.status === "selection_required") {
          setSiteCandidates(json.candidates);
          setData(null);
          return;
        }

        setSiteCandidates(null);
        if ("refreshesUsedToday" in json) setRefreshesUsed(json.refreshesUsedToday);
        if ("limit" in json) setRefreshLimit(json.limit);
        if ("recommendations" in json) setRecommendations(json.recommendations ?? null);

        if (json.status === "rate_limited") {
          toast.error("Daily refresh limit reached", {
            description: `You've used all ${json.limit} manual refreshes for today. The limit resets at midnight UTC.`,
          });
          if (json.data) setData(normalizeDashboardData(json.data));
          return;
        }

        setData(normalizeDashboardData(json.data));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load analytics");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [range, custom]
  );

  useEffect(() => {
    load({ rangeKey: "28d", customRange: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function changeRange(next: DateRangeKey) {
    setRange(next);
    setCustom(null);
    load({ rangeKey: next, customRange: null });
  }

  function applyCustomRange(r: DateRange | undefined) {
    setPickerRange(r);
    if (r?.from && r?.to) {
      const next = { start: toIso(r.from), end: toIso(r.to) };
      setCustom(next);
      setRange("custom");
      setPickerOpen(false);
      load({ rangeKey: "custom", customRange: next });
    }
  }

  const remaining = refreshLimit - refreshesUsed;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-foreground">Website Analytics Dashboard</h1>
          {data && (
            <p className="mt-1 text-sm text-muted-foreground">
              Last updated {fmtLocalTimestamp(data.fetchedAt)}
              {data.fromCache ? " (cached)" : ""}
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            {fmtDateRangeLabel(range, custom)}
            {loading && (
              <span className="ml-2 inline-flex items-center gap-1 text-primary">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
              </span>
            )}
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 lg:w-auto lg:items-end">
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex w-full rounded-md border border-border sm:w-auto">
              {(["7d", "28d", "90d"] as const).map((r) => (
                <button
                  key={r}
                  onClick={() => changeRange(r)}
                  className={`flex-1 px-3 py-1.5 text-sm sm:flex-none ${
                    range === r
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <span className="inline-flex items-center justify-center gap-1.5">
                    {loading && range === r && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    {r === "7d" ? "7 days" : r === "28d" ? "28 days" : "90 days"}
                  </span>
                </button>
              ))}
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger asChild>
                  <button
                    className={`flex-1 px-3 py-1.5 text-sm sm:flex-none ${
                      range === "custom"
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <span className="inline-flex items-center justify-center gap-1.5">
                      {loading && range === "custom" ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <CalendarIcon className="h-3.5 w-3.5" />
                      )}
                      Custom
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="range"
                    selected={pickerRange}
                    onSelect={applyCustomRange}
                    numberOfMonths={2}
                    disabled={{ after: new Date() }}
                    initialFocus
                    className="pointer-events-auto p-3"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <Button
              onClick={() => load({ force: true, gscSiteUrl: selectedSite ?? undefined })}
              disabled={refreshing || loading || remaining <= 0}
              variant="outline"
              className="w-full sm:w-auto"
            >
              {refreshing ? (
                <>
                  <RefreshCw className="mr-1.5 h-4 w-4 animate-spin" />
                  Refreshing…
                </>
              ) : (
                <>Refresh now ({Math.max(remaining, 0)} left today)</>
              )}
            </Button>
          </div>
        </div>
      </div>

      {remaining <= 0 && (
        <p className="mt-3 rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
          Daily manual refresh limit reached. It resets at midnight UTC. The dashboard still loads
          the latest cached data automatically.
        </p>
      )}

      {error && (
        <p className="mt-6 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      )}

      {siteCandidates && (
        <div className="mt-6 rounded-lg border border-border bg-card p-6">
          <h2 className="text-lg font-semibold text-foreground">Choose a Search Console property</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            More than one verified property covers this site. Pick which one to report on.
          </p>
          <div className="mt-4 space-y-2">
            {siteCandidates.map((c) => (
              <button
                key={c}
                onClick={() => {
                  setSelectedSite(c);
                  load({ gscSiteUrl: c });
                }}
                className="block w-full rounded-md border border-border px-4 py-2 text-left text-sm text-foreground hover:bg-accent"
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && !data && <DashboardSkeleton />}

      {data && (
        <div className={`mt-8 space-y-10 transition-opacity ${loading ? "pointer-events-none opacity-50" : ""}`}>
          {(data.gaError || data.gscError) && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
              {data.gaError && <p>Google Analytics: {data.gaError}</p>}
              {data.gscError && <p>Search Console: {data.gscError}</p>}
            </div>
          )}

          {/* 1. Traffic summary */}
          {data.summary && (
            <section>
              <h2 className="text-lg font-semibold text-foreground">Traffic Summary</h2>
              <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
                <SummaryCard
                  label="Users"
                  value={data.summary.users}
                  change={pctChange(data.summary.users, data.summary.prevUsers)}
                  sparklineData={data.timeseries.map((p) => ({ date: p.date, value: p.sessions }))}
                />
                <SummaryCard
                  label="Sessions"
                  value={data.summary.sessions}
                  change={pctChange(data.summary.sessions, data.summary.prevSessions)}
                  sparklineData={data.timeseries.map((p) => ({ date: p.date, value: p.sessions }))}
                />
                <SummaryCard
                  label="Page Views"
                  value={data.summary.pageViews}
                  change={pctChange(data.summary.pageViews, data.summary.prevPageViews)}
                  sparklineData={data.timeseries.map((p) => ({ date: p.date, value: p.views }))}
                />
                <SummaryCard
                  label="Engagement Rate"
                  value={`${(data.summary.engagementRate * 100).toFixed(1)}%`}
                  change={pctChange(data.summary.engagementRate, data.summary.prevEngagementRate)}
                  raw
                />
                {data.searchTotals && (
                  <>
                    <SummaryCard
                      label="Organic Clicks"
                      value={data.searchTotals.clicks}
                      change={null}
                    />
                    <SummaryCard
                      label="Search Impressions"
                      value={data.searchTotals.impressions}
                      change={null}
                    />
                    <SummaryCard
                      label="Search CTR"
                      value={`${(data.searchTotals.ctr * 100).toFixed(2)}%`}
                      change={null}
                      raw
                    />
                    <SummaryCard
                      label="Average Google Position"
                      value={data.searchTotals.position.toFixed(1)}
                      change={null}
                      raw
                    />
                  </>
                )}
              </div>
              <SectionNote>
                How many people visited your site, how engaged they were, and how your site
                performed in Google search results for this date range.
              </SectionNote>
            </section>
          )}

          {/* 1b. Form opt-in conversions */}
          {data.formConversions && (
            <FormConversionsSection conversions={data.formConversions} />
          )}



          {/* 2. Views & sessions over time */}
          {data.timeseries.length > 0 && (
            <section className="rounded-lg border border-border bg-card p-6">
              <h2 className="text-lg font-semibold text-foreground">Views & Sessions Over Time</h2>
              <SectionNote>
                Day-by-day traffic, so you can spot spikes, dips, and overall momentum.
              </SectionNote>
              <div className="mt-4 h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.timeseries.map((p) => ({ ...p, date: fmtDateLabel(p.date) }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" fontSize={12} />
                    <YAxis fontSize={12} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="views" name="Page Views" stroke={PIE_COLORS[0]} dot={false} />
                    <Line type="monotone" dataKey="sessions" name="Sessions" stroke={PIE_COLORS[1]} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <details className="mt-4">
                <summary className="cursor-pointer text-sm text-muted-foreground">Show table</summary>
                <div className="mt-2 max-h-64 overflow-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-muted-foreground">
                        <th className="py-2 pr-4">Date</th>
                        <th className="py-2 pr-4">Sessions</th>
                        <th className="py-2">Page Views</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.timeseries.map((p) => (
                        <tr key={p.date} className="border-b border-border/50">
                          <td className="py-1.5 pr-4">{fmtDateLabel(p.date)}</td>
                          <td className="py-1.5 pr-4">{p.sessions}</td>
                          <td className="py-1.5">{p.views}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </section>
          )}

          {/* 3. Top pages by views */}
          {data.topPages.length > 0 && (
            <SortableTopPagesTable rows={data.topPages} />
          )}

          {/* 4. Top performing pages & posts (Search Console) */}
          {data.searchPages.length > 0 && (
            <section className="rounded-lg border border-border bg-card p-6">
              <h2 className="text-lg font-semibold text-foreground">
                Top Performing Pages & Posts (Google Search)
              </h2>
              <SectionNote>
                Which pages on your site show up in Google search and how many visitors they bring in.
              </SectionNote>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground">
                      <th className="py-2 pr-4">Page</th>
                      <th className="py-2 pr-4">Clicks</th>
                      <th className="py-2 pr-4">Impressions</th>
                      <th className="py-2 pr-4">CTR</th>
                      <th className="py-2">Avg. Position</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.searchPages.slice(0, 25).map((p) => (
                      <tr key={p.page} className="border-b border-border/50">
                        <td className="max-w-md truncate py-1.5 pr-4">{shortPath(p.page)}</td>
                        <td className="py-1.5 pr-4">{p.clicks}</td>
                        <td className="py-1.5 pr-4">{p.impressions}</td>
                        <td className="py-1.5 pr-4">{(p.ctr * 100).toFixed(1)}%</td>
                        <td className="py-1.5">{p.position}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* 5+6. Traffic sources + devices */}
          <div className="grid gap-6 lg:grid-cols-2">
            {data.sources.length > 0 && (
              <section className="rounded-lg border border-border bg-card p-6">
                <h2 className="text-lg font-semibold text-foreground">Traffic Sources</h2>
                <SectionNote>
                  Where your visitors come from — Google search, social media, direct visits, or referrals.
                </SectionNote>
                <div className="mt-4 h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.sources}
                        dataKey="sessions"
                        nameKey="channel"
                        outerRadius={80}
                        label={({ channel, percent }) =>
                          `${channel} ${(percent * 100).toFixed(0)}%`
                        }
                        fontSize={11}
                      >
                        {data.sources.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <table className="mt-4 w-full text-sm">
                  <tbody>
                    {data.sources.map((s) => (
                      <tr key={s.channel} className="border-b border-border/50">
                        <td className="py-1.5 pr-4">{s.channel}</td>
                        <td className="py-1.5 text-right">{s.sessions} sessions</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            {data.devices.length > 0 && (
              <section className="rounded-lg border border-border bg-card p-6">
                <h2 className="text-lg font-semibold text-foreground">Device Category</h2>
                <SectionNote>
                  Whether people browse your site on a phone, desktop computer, or tablet.
                </SectionNote>
                <div className="mt-4 h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.devices}
                        dataKey="sessions"
                        nameKey="device"
                        outerRadius={80}
                        label={({ device, percent }) => `${device} ${(percent * 100).toFixed(0)}%`}
                        fontSize={11}
                      >
                        {data.devices.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <table className="mt-4 w-full text-sm">
                  <tbody>
                    {data.devices.map((d) => (
                      <tr key={d.device} className="border-b border-border/50">
                        <td className="py-1.5 pr-4 capitalize">{d.device}</td>
                        <td className="py-1.5 text-right">{d.sessions} sessions</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
          </div>

          {/* 7. Geography */}
          {data.geo.length > 0 && <GeoSection data={data.geo} />}

          {/* 8+9. Search queries */}
          <div className="grid gap-6 lg:grid-cols-2 min-w-0">
            <div className="min-w-0">
              <SortableQueryTable title="Top Search Queries by Clicks" rows={data.queriesByClicks} />
            </div>
            <div className="min-w-0">
              <SortableQueryTable title="Top Search Queries by Impressions" rows={data.queriesByImpressions} />
            </div>
          </div>

          {/* 9. SEO quick wins */}
          <section className="rounded-lg border border-border bg-card p-6">
            <h2 className="text-lg font-semibold text-foreground">SEO · Quick Wins</h2>
            <SectionNote>
              Standout movements from this period — the highlights worth knowing at a glance.
            </SectionNote>
            {data.quickWins.length === 0 ? (
              <p className="mt-4 text-sm text-muted-foreground">
                No standout movements identified in this date range yet.
              </p>
            ) : (
              <ul className="mt-4 space-y-3">
                {data.quickWins.map((w, i) => (
                  <li
                    key={i}
                    className="border-l-4 border-primary pl-4 py-1"
                  >
                    <p className="font-medium text-foreground">{w.title}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{w.detail}</p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* 10. Recommended next steps */}
          <section className="rounded-lg border border-border bg-card p-6">
            <h2 className="text-lg font-semibold text-foreground">Recommended Next Steps</h2>
            <SectionNote>
              Action items for this site.
            </SectionNote>
            {recommendations && recommendations.items.length > 0 ? (
              <>
                <ul className="mt-4 space-y-3">
                  {recommendations.items.map((item, i) => (
                    <li key={i} className="border-l-4 border-primary pl-4 py-1">
                      <p className="font-medium text-foreground">{item.title}</p>
                      {item.detail ? (
                        <p className="mt-1 text-sm text-muted-foreground">{item.detail}</p>
                      ) : null}
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-xs text-muted-foreground">
                  Last updated{" "}
                  {new Date(recommendations.updatedAt).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </p>
              </>
            ) : (
              <p className="mt-4 text-sm text-muted-foreground">
                No recommendations have been added yet — they'll appear here once your next steps
                are set.
              </p>
            )}
            <p className="mt-4 text-sm text-foreground">
              See a Recommended Next Step you'd like us to take care of? → Send an email with your
              requested next step to{" "}
              <a className="font-medium text-primary underline" href="mailto:hello@threedaylaunch.com">
                hello@threedaylaunch.com
              </a>
              , and we'll get to work!
            </p>
          </section>

          {/* 11. Website & SEO Strategy Spotlight */}
          <section className="rounded-lg border-2 border-primary/40 bg-primary/5 p-6">
            <h2 className="text-lg font-semibold text-foreground">
              Website &amp; SEO Strategy Spotlight
            </h2>
            <SectionNote>
              Fresh insights to help you strengthen your website, improve visibility, and make
              smarter marketing decisions.
            </SectionNote>
            {recommendations?.spotlight && recommendations.spotlight.length > 0 ? (
              <ul className="mt-4 space-y-3">
                {recommendations.spotlight.map((item, i) => (
                  <li key={i} className="rounded-md bg-card/70 border border-border p-4">
                    <p className="font-medium text-foreground">{item.title}</p>
                    {item.detail ? (
                      <p className="mt-1 text-sm text-muted-foreground">{item.detail}</p>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-4 text-sm text-muted-foreground">
                No strategy tips have been added yet — they'll appear here once they're set.
              </p>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

const METRIC_EXPLANATIONS: Record<string, string> = {
  Users: "Unique visitors who initiated at least one session on your site.",
  Sessions: "A group of user interactions that take place within a given time frame.",
  "Page Views": "Total number of pages viewed. Repeated views of a single page are counted.",
  "Engagement Rate": "Percentage of sessions that lasted longer than 10 seconds, had a conversion event, or viewed at least 2 pages.",
  "Organic Clicks": "How many times someone clicked through to your site from Google search results.",
  "Search Impressions": "How many times your pages appeared in Google search results, whether clicked or not.",
  "Search CTR": "Of the people who saw your site in Google, the percentage who clicked it.",
  "Average Google Position": "Your typical ranking spot in Google search results. Lower is better — 1 is the top result.",
};

function SummaryCard({
  label,
  value,
  change,
  raw,
  sparklineData,
  dataKey,
}: {
  label: string;
  value: number | string;
  change: string | null;
  raw?: boolean;
  sparklineData?: { date: string; value: number }[];
  dataKey?: "views" | "sessions";
}) {
  const negative = change?.startsWith("-");
  const neutral = change?.startsWith("+") === false && change?.startsWith("-") === false;
  const TrendIcon = negative ? ArrowDown : neutral ? Minus : ArrowUp;

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <MetricTooltip text={METRIC_EXPLANATIONS[label] ?? ""}>
        <p className="text-sm text-muted-foreground">{label}</p>
      </MetricTooltip>
      <div className="mt-1 flex items-baseline justify-between gap-2">
        <p className="text-2xl font-bold text-foreground">
          {raw ? value : typeof value === "number" ? value.toLocaleString() : value}
        </p>
        {change && (
          <span
            className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-medium ${
              negative
                ? "bg-destructive/10 text-destructive"
                : "bg-green-500/10 text-green-600 dark:text-green-400"
            }`}
          >
            <TrendIcon className="h-3 w-3" />
            {change}
          </span>
        )}
      </div>
      {sparklineData && sparklineData.length > 1 && (
        <div className="mt-3 h-10">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparklineData}>
              <Line
                type="monotone"
                dataKey="value"
                stroke={negative ? "var(--destructive)" : "var(--chart-1)"}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="mt-8 space-y-10 animate-pulse">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-border bg-card p-4">
            <div className="h-4 w-24 rounded bg-muted" />
            <div className="mt-2 h-8 w-20 rounded bg-muted" />
            <div className="mt-2 h-10 rounded bg-muted" />
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-border bg-card p-6">
        <div className="h-6 w-48 rounded bg-muted" />
        <div className="mt-4 h-72 rounded bg-muted" />
      </div>
    </div>
  );
}

function SortableTopPagesTable({
  rows,
}: {
  rows: { path: string; views: number; users: number; avgEngagementSeconds: number }[];
}) {
  const { sorted, sort, toggle } = useSort(rows);

  return (
    <section className="rounded-lg border border-border bg-card p-6">
      <h2 className="text-lg font-semibold text-foreground">Top Website Pages by Views</h2>
      <SectionNote>
        The pages people look at most once they are on your site.
      </SectionNote>
      <div className="mt-4 w-full overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4">
                <SortHeader label="Page" active={sort.key === "path"} dir={sort.dir} onClick={() => toggle("path")} />
              </th>
              <th className="py-2 pr-4">
                <SortHeader label="Views" active={sort.key === "views"} dir={sort.dir} onClick={() => toggle("views")} />
              </th>
              <th className="py-2 pr-4">
                <SortHeader label="Users" active={sort.key === "users"} dir={sort.dir} onClick={() => toggle("users")} />
              </th>
              <th className="py-2">
                <SortHeader label="Avg. Engagement" active={sort.key === "avgEngagementSeconds"} dir={sort.dir} onClick={() => toggle("avgEngagementSeconds")} />
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => (
              <tr key={p.path} className="border-b border-border/50">
                <td className="max-w-md truncate py-1.5 pr-4">{p.path}</td>
                <td className="py-1.5 pr-4">{p.views.toLocaleString()}</td>
                <td className="py-1.5 pr-4">{p.users.toLocaleString()}</td>
                <td className="py-1.5">{fmtDuration(p.avgEngagementSeconds)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SortableQueryTable({
  title,
  rows,
}: {
  title: string;
  rows: { query: string; clicks: number; impressions: number; ctr: number; position: number }[];
}) {
  const { sorted, sort, toggle } = useSort(rows);

  return (
    <section className="min-w-0 overflow-hidden rounded-lg border border-border bg-card p-6">
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      <SectionNote>
        {title.includes("Impressions")
          ? "Keyword phrases people searched where your site appeared in Google — even if they did not click yet."
          : "The keyword phrases people search that lead them to click through to your website."}
      </SectionNote>
      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">No search data for this range.</p>
      ) : (
        <div className="mt-4 max-h-96 w-full overflow-x-auto overflow-y-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="min-w-64 whitespace-nowrap py-2 pr-3">
                  <SortHeader label="Query" active={sort.key === "query"} dir={sort.dir} onClick={() => toggle("query")} />
                </th>
                <th className="whitespace-nowrap py-2 pr-3">
                  <SortHeader label="Clicks" active={sort.key === "clicks"} dir={sort.dir} onClick={() => toggle("clicks")} />
                </th>
                <th className="whitespace-nowrap py-2 pr-3">
                  <SortHeader label="Impr." active={sort.key === "impressions"} dir={sort.dir} onClick={() => toggle("impressions")} />
                </th>
                <th className="whitespace-nowrap py-2 pr-3">
                  <SortHeader label="CTR" active={sort.key === "ctr"} dir={sort.dir} onClick={() => toggle("ctr")} />
                </th>
                <th className="whitespace-nowrap py-2">
                  <SortHeader label="Pos." active={sort.key === "position"} dir={sort.dir} onClick={() => toggle("position")} />
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((q) => (
                <tr key={q.query} className="border-b border-border/50">
                  <td className="min-w-64 whitespace-nowrap py-1.5 pr-3">{q.query}</td>
                  <td className="whitespace-nowrap py-1.5 pr-3">{q.clicks.toLocaleString()}</td>
                  <td className="whitespace-nowrap py-1.5 pr-3">{q.impressions.toLocaleString()}</td>
                  <td className="whitespace-nowrap py-1.5 pr-3">{(q.ctr * 100).toFixed(1)}%</td>
                  <td className="whitespace-nowrap py-1.5">{q.position.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

function GeoSection({ data }: { data: { country: string; sessions: number; views: number }[] }) {
  const total = data.reduce((sum, d) => sum + d.sessions, 0);
  const [tooltip, setTooltip] = useState<{ country: string; value: number } | null>(null);
  const maxSessions = Math.max(...data.map((d) => d.sessions), 1);

  const lookup = useMemo(() => {
    const map = new Map<string, number>();
    data.forEach((d) => map.set(d.country, d.sessions));
    return map;
  }, [data]);

  return (
    <section className="rounded-lg border border-border bg-card p-6">
      <h2 className="text-lg font-semibold text-foreground">Views & Sessions by Location</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Total sessions mapped by country. Darker shading = more sessions.
      </p>
      <div className="mt-4 grid gap-6 lg:grid-cols-3">
        <div className="relative overflow-hidden lg:col-span-2">
          <div className="h-80 w-full overflow-hidden">
            <ComposableMap projectionConfig={{ scale: 147 }} projection="geoMercator">
              <ZoomableGroup>
                <Geographies geography={GEO_URL}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const name = geo.properties?.name as string;
                      const value = lookup.get(name) ?? 0;
                      const opacity = value > 0 ? Math.max(0.2, value / maxSessions) : 0.05;
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={value > 0 ? `rgba(59, 130, 246, ${opacity})` : "var(--muted)"}
                          stroke="var(--border)"
                          strokeWidth={0.5}
                          onMouseEnter={() => value > 0 && setTooltip({ country: name, value })}
                          onMouseLeave={() => setTooltip(null)}
                          onClick={() =>
                            setTooltip((prev) =>
                              prev?.country !== name ? { country: name, value } : null,
                            )
                          }
                          onTouchStart={() => setTooltip({ country: name, value })}


                          style={{
                            default: { outline: "none" },
                            hover: { fill: "var(--primary)", outline: "none" },
                            pressed: { outline: "none" },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>
          </div>
          {tooltip && (
            <div className="pointer-events-none absolute left-1/2 top-4 z-10 -translate-x-1/2 rounded-md border border-border bg-popover px-2 py-1 text-xs text-popover-foreground shadow-lg">
              {tooltip.country}: {tooltip.value.toLocaleString()} sessions
            </div>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="py-2 pr-4">Country</th>
                <th className="py-2 pr-4">Sessions</th>
                <th className="py-2">Share</th>
              </tr>
            </thead>
            <tbody>
              {data.slice(0, 20).map((d) => (
                <tr key={d.country} className="border-b border-border/50">
                  <td className="py-1.5 pr-4">{d.country}</td>
                  <td className="py-1.5 pr-4">{d.sessions.toLocaleString()}</td>
                  <td className="py-1.5">{total > 0 ? `${((d.sessions / total) * 100).toFixed(1)}%` : "0%"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
